import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckOutlined,
  ReloadOutlined,
  SettingOutlined,
  DownloadOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import {
  fetchTaxDeclaration,
  fetchTaxWithholdings,
  fetchMerchantTaxSettings,
  updateMerchantTaxSettings,
  markTaxRemitted,
  fetchTaxStats,
  backfillTaxWithholdings,
  downloadTaxDeclaration,
  fetchMerchantTaxSettingsList,
  type DeclarationSummary,
  type TaxWithholdingRow,
  type MerchantTaxSettings,
  type TaxCategory,
  type BusinessClassification,
} from '../../services/merchantTaxService';
import {
  formatCurrencyVND,
  formatDateTimeVN,
} from '../../utils/transactionHelpers';
import { vnDayjs } from '../../utils/vnDateTime';
import { spacing } from '../../design-system/tokens';

const { Title, Text, Paragraph } = Typography;

const CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

type PeriodMode = 'month' | 'quarter' | 'year';

function currentVnPeriod() {
  const now = vnDayjs();
  return {
    year: now.year(),
    month: now.month() + 1,
    quarter: Math.ceil((now.month() + 1) / 3),
  };
}

function defaultExportRange(): [Dayjs, Dayjs] {
  const now = vnDayjs();
  return [now.startOf('month'), now.endOf('month')];
}

function pct(rate: number): string {
  return `${Math.round(rate * 10000) / 100}%`;
}

export default function MerchantTaxPage() {
  const initial = currentVnPeriod();
  const [periodMode, setPeriodMode] = useState<PeriodMode>('month');
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);
  const [quarter, setQuarter] = useState(initial.quarter);
  const [merchantId, setMerchantId] = useState<number | undefined>();
  const [declaration, setDeclaration] = useState<DeclarationSummary | null>(
    null
  );
  const [txRows, setTxRows] = useState<TaxWithholdingRow[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txSummary, setTxSummary] = useState<
    DeclarationSummary['summary'] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsMerchantId, setSettingsMerchantId] = useState<number | null>(
    null
  );
  const [settings, setSettings] = useState<MerchantTaxSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [withheldTotal, setWithheldTotal] = useState<number | null>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMerchantId, setExportMerchantId] = useState<
    number | undefined
  >();
  const [exportRange, setExportRange] = useState<[Dayjs, Dayjs] | null>(
    defaultExportRange()
  );
  const [exportMerchantOptions, setExportMerchantOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [exportLoading, setExportLoading] = useState<'csv' | 'pdf' | null>(
    null
  );
  const [form] = Form.useForm();

  const periodParams = useMemo(() => {
    const base: Record<string, number | string | undefined> = {
      period: periodMode,
      year,
      merchantId,
    };
    if (periodMode === 'month') base.month = month;
    if (periodMode === 'quarter') base.quarter = quarter;
    return base;
  }, [periodMode, year, month, quarter, merchantId]);

  const loadDeclaration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTaxDeclaration({
        ...periodParams,
        page: 1,
        limit: 100,
      });
      setDeclaration(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không tải được tờ khai thuế');
    } finally {
      setLoading(false);
    }
  }, [periodParams]);

  const loadTransactions = useCallback(
    async (page = 1) => {
      try {
        setTxLoading(true);
        const res = await fetchTaxWithholdings({
          ...periodParams,
          status: 'WITHHELD',
          page,
          limit: 20,
        });
        setTxRows(res.data);
        setTxTotal(res.pagination.total);
        setTxPage(page);
        setTxSummary(res.summary || null);
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || 'Không tải được giao dịch thuế'
        );
      } finally {
        setTxLoading(false);
      }
    },
    [periodParams]
  );

  useEffect(() => {
    loadDeclaration();
    loadTransactions(1);
    setSelectedIds([]);
    fetchTaxStats()
      .then(s => setWithheldTotal(s.withheldOrderCount))
      .catch(() => setWithheldTotal(null));
  }, [loadDeclaration, loadTransactions]);

  const openExportModal = async () => {
    setExportOpen(true);
    setExportMerchantId(merchantId);
    setExportRange(defaultExportRange());
    try {
      const res = await fetchMerchantTaxSettingsList({ limit: 500, page: 1 });
      const fromSettings = (res.data || [])
        .filter(row => row.restaurantId)
        .map(row => ({
          value: row.restaurantId,
          label: row.restaurantName
            ? `${row.restaurantName} (#${row.restaurantId})`
            : `Merchant #${row.restaurantId}`,
        }));
      const fromDeclaration = (declaration?.merchants || []).map(m => ({
        value: m.merchantId,
        label: m.restaurantName
          ? `${m.restaurantName} (#${m.merchantId})`
          : `Merchant #${m.merchantId}`,
      }));
      const merged = new Map<number, { value: number; label: string }>();
      [...fromSettings, ...fromDeclaration].forEach(item => {
        merged.set(item.value, item);
      });
      setExportMerchantOptions(
        Array.from(merged.values()).sort((a, b) =>
          a.label.localeCompare(b.label, 'vi')
        )
      );
    } catch {
      setExportMerchantOptions(
        (declaration?.merchants || []).map(m => ({
          value: m.merchantId,
          label: m.restaurantName
            ? `${m.restaurantName} (#${m.merchantId})`
            : `Merchant #${m.merchantId}`,
        }))
      );
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    if (!exportRange?.[0] || !exportRange?.[1]) {
      message.warning('Chọn khoảng ngày để xuất');
      return;
    }
    setExportLoading(format);
    try {
      await downloadTaxDeclaration(
        {
          merchantId: exportMerchantId,
          from: exportRange[0].startOf('day').toISOString(),
          to: exportRange[1].endOf('day').toISOString(),
        },
        format
      );
      message.success(`Đã tải file ${format.toUpperCase()}`);
      setExportOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Xuất dữ liệu thất bại');
    } finally {
      setExportLoading(null);
    }
  };
  const handleBackfill = async () => {
    Modal.confirm({
      title: 'Backfill dữ liệu thuế lịch sử',
      content:
        'Hệ thống sẽ quét tất cả đơn PAID (online + COD) và tạo sổ khấu trừ thuế cho các đơn chưa có. Chạy một lần sau khi bật tính năng hoặc migrate.',
      okText: 'Chạy backfill',
      cancelText: 'Hủy',
      onOk: async () => {
        setBackfillLoading(true);
        try {
          const result = await backfillTaxWithholdings();
          message.success(
            `Backfill xong: ${result.backfilled} đơn mới, ${result.skipped} bỏ qua, ${result.errors} lỗi`
          );
          setWithheldTotal(prev => (prev ?? 0) + result.backfilled);
          loadDeclaration();
          loadTransactions(1);
        } catch (err: any) {
          message.error(
            err?.response?.data?.message ||
              'Backfill thất bại — kiểm tra migration đã chạy chưa'
          );
        } finally {
          setBackfillLoading(false);
        }
      },
    });
  };

  const openSettings = async (id: number) => {
    setSettingsMerchantId(id);
    setSettingsOpen(true);
    setSettingsLoading(true);
    try {
      const data = await fetchMerchantTaxSettings(id);
      setSettings(data);
      form.setFieldsValue({
        withholdingEnabled: data.withholdingEnabled,
        taxCategory: data.taxCategory,
        vatRatePercent: Math.round(data.vatRate * 10000) / 100,
        pitRatePercent: Math.round(data.pitRate * 10000) / 100,
        businessClassification: data.businessClassification || 'household',
        taxId: data.taxId || '',
        ownerFullName: data.ownerFullName || '',
        notes: data.notes || '',
      });
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Không tải được cấu hình thuế'
      );
      setSettingsOpen(false);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async (values: any) => {
    if (!settingsMerchantId) return;
    try {
      const category = values.taxCategory as TaxCategory;
      await updateMerchantTaxSettings(settingsMerchantId, {
        withholdingEnabled: values.withholdingEnabled,
        taxCategory: category,
        vatRate:
          category === 'custom'
            ? Number(values.vatRatePercent) / 100
            : undefined,
        pitRate:
          category === 'custom'
            ? Number(values.pitRatePercent) / 100
            : undefined,
        businessClassification:
          values.businessClassification as BusinessClassification,
        taxId: values.taxId?.trim() || null,
        ownerFullName: values.ownerFullName?.trim() || null,
        notes: values.notes?.trim() || null,
      });
      message.success('Đã cập nhật cấu hình thuế');
      setSettingsOpen(false);
      loadDeclaration();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Cập nhật thất bại');
    }
  };

  const handleMarkRemitted = async (scope: 'selected' | 'period') => {
    try {
      const body =
        scope === 'selected'
          ? { ids: selectedIds, note: 'Đánh dấu đã nộp từ admin' }
          : {
              merchantId,
              year,
              month: periodMode === 'month' ? month : undefined,
              quarter: periodMode === 'quarter' ? quarter : undefined,
              note: 'Đánh dấu đã nộp theo kỳ từ admin',
            };
      const res = await markTaxRemitted(body);
      message.success(`Đã đánh dấu nộp thuế ${res.updated} giao dịch`);
      setSelectedIds([]);
      loadDeclaration();
      loadTransactions(txPage);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Không đánh dấu được');
    }
  };

  const summary = declaration?.summary;
  const yearOptions = Array.from({ length: 6 }, (_, i) => initial.year - i);

  const merchantColumns: ColumnsType<DeclarationSummary['merchants'][number]> =
    [
      {
        title: 'Quán',
        dataIndex: 'restaurantName',
        render: (name, row) => (
          <Space direction="vertical" size={0}>
            <Link to={`/merchants/${row.merchantId}`}>
              {name || `#${row.merchantId}`}
            </Link>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID {row.merchantId}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Số đơn',
        dataIndex: 'orderCount',
        width: 90,
        align: 'right',
      },
      {
        title: 'Doanh thu tính thuế',
        dataIndex: 'taxBaseVnd',
        align: 'right',
        render: v => formatCurrencyVND(v),
      },
      {
        title: 'GTGT 3%',
        dataIndex: 'vatAmountVnd',
        align: 'right',
        render: v => formatCurrencyVND(v),
      },
      {
        title: 'TNCN 1,5%',
        dataIndex: 'pitAmountVnd',
        align: 'right',
        render: v => formatCurrencyVND(v),
      },
      {
        title: 'Tổng khấu trừ',
        dataIndex: 'totalTaxVnd',
        align: 'right',
        render: v => <strong>{formatCurrencyVND(v)}</strong>,
      },
      {
        title: 'Chưa nộp',
        dataIndex: 'pendingRemittanceVnd',
        align: 'right',
        render: v =>
          v > 0 ? (
            <Tag color="orange">{formatCurrencyVND(v)}</Tag>
          ) : (
            <Tag color="green">Đã nộp</Tag>
          ),
      },
      {
        title: '',
        width: 160,
        render: (_, row) => (
          <Space>
            <Button
              size="small"
              onClick={() => {
                setMerchantId(row.merchantId);
              }}
            >
              Xem GD
            </Button>
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => openSettings(row.merchantId)}
            />
          </Space>
        ),
      },
    ];

  const txColumns: ColumnsType<TaxWithholdingRow> = [
    {
      title: 'Thời điểm',
      dataIndex: 'withheldAt',
      width: 140,
      render: v => formatDateTimeVN(v),
    },
    {
      title: 'Đơn',
      dataIndex: 'orderNumber',
      render: (code, row) => (
        <Link to={`/orders/${row.orderId}`}>{code || `#${row.orderId}`}</Link>
      ),
    },
    {
      title: 'Quán',
      dataIndex: 'restaurantName',
      render: (name, row) => (
        <Link to={`/merchants/${row.merchantId}`}>
          {name || `#${row.merchantId}`}
        </Link>
      ),
    },
    {
      title: 'Doanh thu',
      dataIndex: 'taxBaseVnd',
      align: 'right',
      render: v => formatCurrencyVND(v),
    },
    {
      title: 'GTGT',
      dataIndex: 'vatAmountVnd',
      align: 'right',
      render: v => formatCurrencyVND(v),
    },
    {
      title: 'TNCN',
      dataIndex: 'pitAmountVnd',
      align: 'right',
      render: v => formatCurrencyVND(v),
    },
    {
      title: 'Tổng thuế',
      dataIndex: 'totalTaxVnd',
      align: 'right',
      render: v => <strong>{formatCurrencyVND(v)}</strong>,
    },
    {
      title: 'Merchant nhận',
      dataIndex: 'merchantNetAmount',
      align: 'right',
      render: v => (v != null ? formatCurrencyVND(v) : '—'),
    },
    {
      title: 'Nộp thuế',
      dataIndex: 'remittanceStatus',
      render: s =>
        s === 'REMITTED' ? (
          <Tag color="green">Đã nộp</Tag>
        ) : (
          <Tag color="orange">Chưa nộp</Tag>
        ),
    },
  ];

  return (
    <div style={{ padding: spacing.lg }}>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
        wrap
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Thuế hộ / cá nhân kinh doanh
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Khấu trừ GTGT 3% + TNCN 1,5% (tổng 4,5%) trên mỗi đơn thành công —
            hỗ trợ đối chiếu tờ khai tháng / quý / năm.
          </Paragraph>
        </div>
        <Space wrap>
          <Select
            value={periodMode}
            style={{ width: 120 }}
            onChange={(v: PeriodMode) => setPeriodMode(v)}
            options={[
              { value: 'month', label: 'Theo tháng' },
              { value: 'quarter', label: 'Theo quý' },
              { value: 'year', label: 'Theo năm' },
            ]}
          />
          <Select
            value={year}
            style={{ width: 100 }}
            onChange={setYear}
            options={yearOptions.map(y => ({ value: y, label: String(y) }))}
          />
          {periodMode === 'month' && (
            <Select
              value={month}
              style={{ width: 110 }}
              onChange={setMonth}
              options={Array.from({ length: 12 }, (_, i) => ({
                value: i + 1,
                label: `Tháng ${i + 1}`,
              }))}
            />
          )}
          {periodMode === 'quarter' && (
            <Select
              value={quarter}
              style={{ width: 100 }}
              onChange={setQuarter}
              options={[1, 2, 3, 4].map(q => ({ value: q, label: `Quý ${q}` }))}
            />
          )}
          <Input
            placeholder="Lọc merchant ID"
            style={{ width: 140 }}
            value={merchantId ?? ''}
            onChange={e => {
              const raw = e.target.value.trim();
              setMerchantId(raw ? parseInt(raw, 10) || undefined : undefined);
            }}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadDeclaration();
              loadTransactions(1);
            }}
          >
            Tải lại
          </Button>
          <Button
            icon={<DatabaseOutlined />}
            loading={backfillLoading}
            onClick={handleBackfill}
          >
            Backfill đơn cũ
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={openExportModal}
          >
            Xuất dữ liệu
          </Button>
        </Space>
      </Space>

      {withheldTotal === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Chưa có sổ khấu trừ thuế"
          description="Dữ liệu chỉ tự ghi cho đơn mới sau khi bật tính năng. Bấm «Backfill đơn cũ» để import đơn PAID lịch sử (~1 năm), hoặc chạy migration trên server: npx knex migrate:latest"
        />
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {declaration?.regulation && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={declaration.regulation.label}
          description={`${declaration.regulation.note} · GTGT ${pct(
            declaration.regulation.vatRate
          )} + TNCN ${pct(declaration.regulation.pitRate)} = ${pct(
            declaration.regulation.totalRate
          )}`}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={CARD_STYLE} loading={loading}>
            <Statistic
              title="Số đơn đã khấu trừ"
              value={summary?.orderCount || 0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={CARD_STYLE} loading={loading}>
            <Statistic
              title="Doanh thu tính thuế"
              value={summary?.taxableRevenueVnd ?? summary?.taxBaseVnd ?? 0}
              formatter={v => formatCurrencyVND(Number(v))}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={CARD_STYLE} loading={loading}>
            <Statistic
              title="GTGT phải nộp"
              value={summary?.vatPayableVnd ?? summary?.vatAmountVnd ?? 0}
              formatter={v => formatCurrencyVND(Number(v))}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={CARD_STYLE} loading={loading}>
            <Statistic
              title="TNCN phải nộp"
              value={summary?.pitPayableVnd ?? summary?.pitAmountVnd ?? 0}
              formatter={v => formatCurrencyVND(Number(v))}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} loading={loading}>
        <Space
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
          wrap
        >
          <Text>
            Tổng thuế đã khấu trừ:{' '}
            <strong>
              {formatCurrencyVND(
                summary?.totalWithheldVnd ?? summary?.totalTaxVnd ?? 0
              )}
            </strong>
            {' · '}
            Chưa nộp NSNN:{' '}
            <strong>
              {formatCurrencyVND(summary?.pendingRemittanceVnd || 0)}
            </strong>
          </Text>
          <Space>
            <Button
              icon={<CheckOutlined />}
              disabled={!selectedIds.length}
              onClick={() => handleMarkRemitted('selected')}
            >
              Đánh dấu đã nộp ({selectedIds.length})
            </Button>
            <Button onClick={() => handleMarkRemitted('period')}>
              Đánh dấu đã nộp cả kỳ{merchantId ? ' (merchant)' : ''}
            </Button>
          </Space>
        </Space>
      </Card>

      <Card style={CARD_STYLE}>
        <Tabs
          items={[
            {
              key: 'merchants',
              label: 'Theo merchant (tờ khai)',
              children: (
                <Table
                  rowKey="merchantId"
                  loading={loading}
                  columns={merchantColumns}
                  dataSource={declaration?.merchants || []}
                  pagination={false}
                  scroll={{ x: 1000 }}
                />
              ),
            },
            {
              key: 'transactions',
              label: 'Giao dịch trong kỳ',
              children: (
                <>
                  {txSummary && (
                    <Text
                      type="secondary"
                      style={{ display: 'block', marginBottom: 12 }}
                    >
                      {txSummary.orderCount} giao dịch · GTGT{' '}
                      {formatCurrencyVND(txSummary.vatAmountVnd)} · TNCN{' '}
                      {formatCurrencyVND(txSummary.pitAmountVnd)} · Tổng{' '}
                      {formatCurrencyVND(txSummary.totalTaxVnd)}
                    </Text>
                  )}
                  <Table
                    rowKey="id"
                    loading={txLoading}
                    columns={txColumns}
                    dataSource={txRows}
                    rowSelection={{
                      selectedRowKeys: selectedIds,
                      onChange: keys => setSelectedIds(keys as number[]),
                      getCheckboxProps: row => ({
                        disabled: row.remittanceStatus === 'REMITTED',
                      }),
                    }}
                    pagination={{
                      current: txPage,
                      pageSize: 20,
                      total: txTotal,
                      onChange: p => loadTransactions(p),
                    }}
                    scroll={{ x: 1100 }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Xuất bảng kê khấu trừ thuế"
        open={exportOpen}
        onCancel={() => setExportOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setExportOpen(false)}>Hủy</Button>
            <Button
              icon={<DownloadOutlined />}
              loading={exportLoading === 'csv'}
              onClick={() => handleExport('csv')}
            >
              Xuất CSV
            </Button>
            <Button
              type="primary"
              loading={exportLoading === 'pdf'}
              onClick={() => handleExport('pdf')}
            >
              Xuất PDF
            </Button>
          </Space>
        }
        destroyOnClose
        width={520}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              Merchant
            </Text>
            <Select
              allowClear
              showSearch
              placeholder="Tất cả merchant"
              style={{ width: '100%' }}
              value={exportMerchantId}
              onChange={v => setExportMerchantId(v ?? undefined)}
              optionFilterProp="label"
              options={exportMerchantOptions}
            />
          </div>
          <div>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              Khoảng ngày khấu trừ
            </Text>
            <DatePicker.RangePicker
              value={exportRange || undefined}
              onChange={dates =>
                setExportRange(
                  dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null
                )
              }
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </div>
          <Alert
            type="info"
            showIcon
            message="Dữ liệu xuất theo bộ lọc trên"
            description="CSV: dữ liệu thô để đối chiếu. PDF: bảng kê có định dạng in (tiếng Việt đầy đủ)."
          />
        </Space>
      </Modal>

      <Modal
        title={`Cấu hình thuế merchant`}
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={settingsLoading}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={saveSettings}>
          <Form.Item
            name="withholdingEnabled"
            label="Khấu trừ thuế"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: true, label: 'Bật (hộ / cá nhân kinh doanh)' },
                { value: false, label: 'Tắt' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="taxCategory"
            label="Loại thuế"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                {
                  value: 'goods_with_transport',
                  label: 'Vận tải / DV gắn hàng hóa (GTGT 3% + TNCN 1,5%)',
                },
                { value: 'custom', label: 'Tùy chỉnh tỷ lệ' },
                { value: 'exempt', label: 'Miễn khấu trừ (DN kê khai riêng)' },
              ]}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.taxCategory !== cur.taxCategory}
          >
            {({ getFieldValue }) =>
              getFieldValue('taxCategory') === 'custom' ? (
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="vatRatePercent"
                      label="GTGT (%)"
                      rules={[{ required: true }]}
                    >
                      <Input type="number" min={0} max={100} step={0.1} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="pitRatePercent"
                      label="TNCN (%)"
                      rules={[{ required: true }]}
                    >
                      <Input type="number" min={0} max={100} step={0.1} />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>
          <Form.Item name="businessClassification" label="Phân loại">
            <Select
              options={[
                { value: 'household', label: 'Hộ kinh doanh' },
                { value: 'individual', label: 'Cá nhân kinh doanh' },
                { value: 'enterprise', label: 'Doanh nghiệp' },
                { value: 'unknown', label: 'Chưa rõ' },
              ]}
            />
          </Form.Item>
          <Form.Item name="taxId" label="MST / CCCD thuế">
            <Input placeholder="Mã số thuế" />
          </Form.Item>
          <Form.Item name="ownerFullName" label="Tên chủ hộ / cá nhân">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
